import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Typography } from '@/components/ui/typography'
import { useCorrectDealLead, useNameDealLeads } from '@/hooks/use-deal-leads'
import { showError, showSuccess } from '@/lib/toast'
import { formatReceivedAt } from '@/features/deal-review/deal-lead-fields'
import type { DealLeadListItem } from '@/types/api'

export function AddNamePopover({
  lead,
}: {
  lead: { id: string; version: number }
}) {
  const save = useCorrectDealLead(lead.id)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const trimmed = name.trim()

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={save.isPending}
          className="h-6 gap-1 rounded-full px-2 text-xs"
        >
          <Plus aria-hidden="true" className="size-3" />
          {save.isPending ? 'Saving…' : 'Add name'}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3">
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (!trimmed) return
            setOpen(false)
            save.mutate(
              { version: lead.version, companyName: trimmed },
              {
                onSuccess: () => setName(''),
                onError: (error) =>
                  showError(error, 'The name could not be saved'),
              }
            )
          }}
        >
          <Typography as="span" variant="small" className="text-primary/60">
            DealCloud needs a name to send this deal
          </Typography>
          <Input
            autoFocus
            aria-label="Company name"
            placeholder="Company name"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Button type="submit" size="sm" disabled={!trimmed}>
            Save
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  )
}

export function AddNamesDialog({
  leads,
  open,
  onOpenChange,
}: {
  leads: DealLeadListItem[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const save = useNameDealLeads()
  const [names, setNames] = useState<Record<string, string>>({})
  const filled = leads.flatMap((lead) => {
    const name = names[lead.id]?.trim()
    return name ? [{ lead, name }] : []
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setNames({})
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-150 px-4 py-5">
        <DialogHeader className="mb-4">
          <DialogTitle>Add names</DialogTitle>
          <DialogDescription>
            DealCloud needs a name to send{' '}
            {leads.length === 1 ? 'this deal' : 'these deals'}. Leave a field
            empty to skip it.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault()
            if (filled.length === 0) return
            save.mutate(filled, {
              onSuccess: (unsaved) => {
                const saved = filled.length - unsaved
                if (saved > 0) {
                  showSuccess(
                    `${saved} ${saved === 1 ? 'name' : 'names'} saved.`
                  )
                }
                if (unsaved > 0) {
                  showError(
                    undefined,
                    `${unsaved} ${unsaved === 1 ? 'name was' : 'names were'} not saved.`
                  )
                  return
                }
                setNames({})
                onOpenChange(false)
              },
            })
          }}
        >
          <ul className="flex max-h-[50vh] flex-col overflow-y-auto">
            {leads.map((lead) => (
              <li
                key={lead.id}
                className="flex flex-col gap-1.5 border-b border-primary/5 py-3 first:pt-0 last:border-b-0"
              >
                <UnnamedIdentity lead={lead} />
                <Input
                  aria-label={`Company name for ${identityOf(lead)}`}
                  placeholder="Company name"
                  value={names[lead.id] ?? ''}
                  onChange={(event) =>
                    setNames((current) => ({
                      ...current,
                      [lead.id]: event.target.value,
                    }))
                  }
                />
              </li>
            ))}
          </ul>
          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={filled.length === 0 || save.isPending}
            >
              {save.isPending
                ? 'Saving…'
                : filled.length === 0
                  ? 'Save names'
                  : `Save ${filled.length} ${filled.length === 1 ? 'name' : 'names'}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// With no name, the email is what tells a reviewer which deal this is.
export function UnnamedIdentity({
  lead,
}: {
  lead: {
    subject?: string
    sourceFileName?: string
    receivedAt: string
  }
}) {
  return (
    <div className="min-w-0">
      <Typography as="span" variant="body-sm" className="block text-primary/60">
        Unnamed deal · {formatReceivedAt(lead.receivedAt)}
      </Typography>
      <Typography
        as="span"
        variant="body-sm"
        className="line-clamp-1 text-primary"
        title={identityOf(lead)}
      >
        {identityOf(lead)}
      </Typography>
    </div>
  )
}

function identityOf(lead: {
  subject?: string
  sourceFileName?: string
}): string {
  return (
    [lead.subject, lead.sourceFileName].filter(Boolean).join(' — ') ||
    'No subject'
  )
}
